import React, { useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { 
  ArrowRight, ShieldCheck, Zap, CheckCircle2, Star, TrendingUp, 
  Building2, Globe, Heart, Layers, FileText, PiggyBank, Compass, Car, 
  Landmark, ArrowUpRight, Languages, Scan, FileCheck, Lock, X, Smartphone
} from 'lucide-react';
import { motion } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';
import HeaderActions from '../components/HeaderActions';
import FoxLogo from '../components/FoxLogo';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import TrustBar from '../components/TrustBar';

// 🔥 TİLKİ BİLEŞENLERİ 🔥
import PlayfulFox from '../components/PlayfulFox';
import { useFox } from '../context/FoxContext';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // 🔥 Dil Fonksiyonunu Al
  const { t } = useLanguage(); 
  
  // 🔥 Tilki Tetikleyici 🔥
  const { triggerFox } = useFox();

  // Sayfa açılınca Tilkiyi Çağır
  useEffect(() => {
    const timer = setTimeout(() => {
        triggerFox('fox_welcome_guest', { // "Merhaba! Finansal özgürlüğe hazır mısın?"
            position: 'bottom-right', 
            mood: 'excited',
            action: '/login' 
        });
    }, 2000); // 2 saniye sonra çıksın

    return () => clearTimeout(timer);
  }, [triggerFox]);

  if (user) return <Navigate to="/dashboard" replace />;

  const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 80 } },
  };

  return (
    // Global Arka Plan (#0B1120) - App.css ile uyumlu
    <div className="min-h-screen font-sans text-slate-100 overflow-x-hidden relative bg-[#0B1120]">
      
      {/* Background Decor - Ambient Lights */}
      <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-blue-600/10 rounded-full blur-[180px] opacity-50 mix-blend-screen"></div>
          <div className="absolute bottom-[-20%] right-[-10%] w-[700px] h-[700px] bg-orange-600/10 rounded-full blur-[150px] opacity-40 mix-blend-screen"></div>
      </div>

      {/* HEADER - Soft Glass */}
      <header className="fixed top-0 w-full z-50 px-4 md:px-6 py-4 transition-all duration-300">
        <div className="max-w-7xl mx-auto bg-[#0B1120]/80 backdrop-blur-xl border border-white/10 rounded-2xl px-4 md:px-6 py-3 flex items-center justify-between shadow-2xl">
            
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="bg-gradient-to-br from-orange-600 to-orange-500 p-2 rounded-xl shadow-lg shadow-orange-600/20 group-hover:scale-110 transition-transform duration-300">
                 <FoxLogo size={20} className="text-white" />
              </div>
              <span className="text-xl font-black tracking-tighter uppercase italic text-white leading-none hidden md:block">
                  Büro<span className="text-orange-500">Fuchs</span>
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 md:gap-6">
              <HeaderActions />
              <Button 
                onClick={() => navigate('/login')} 
                className="h-10 px-4 md:px-6 bg-white hover:bg-blue-50 text-[#0B1120] rounded-xl font-black uppercase tracking-widest text-[10px] transition-all hover:scale-105 active:scale-95 shadow-lg whitespace-nowrap"
              >
                {t('cta_signup') || "Sign Up"}
              </Button>
            </div>
        </div>
      </header>

      <main className="relative z-10 pt-36 pb-20 space-y-40">
        
        {/* 1. HERO SECTION: THE PROMISE */}
        <section className="px-6 relative text-center">
           <motion.div 
             initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
             className="max-w-5xl mx-auto relative z-10"
           >
                 <Badge className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 px-6 py-2 rounded-full text-xs font-black uppercase tracking-[0.2em] mb-8 shadow-lg shadow-blue-900/50 animate-pulse">
                   {t('hero_badge') || "Germany's First Super App"}
                 </Badge>
                 
                 <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter leading-[0.9] mb-6 drop-shadow-2xl">
                   {t('hero_title_1') || "One App."}<br/>
                   <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500">{t('hero_title_2') || "Limitless Capability."}</span>
                 </h1>
                 
                 <p className="text-lg md:text-2xl text-slate-300 font-medium max-w-3xl mx-auto leading-relaxed mb-12">
                   {t('hero_desc') || "Get tax refunds, manage bureaucracy, issue invoices. BüroFuchs is your digital HQ."}
                 </p>

                 <div className="flex flex-col sm:flex-row gap-5 justify-center items-center relative z-10">
                    <Button onClick={() => navigate('/login')} className="h-16 px-12 bg-white text-[#0B1120] hover:bg-slate-200 rounded-2xl font-black text-sm uppercase tracking-widest shadow-[0_0_40px_rgba(255,255,255,0.15)] transition-all hover:scale-105 active:scale-95 group">
                        {t('cta_start_free') || "Start for Free"} <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    
                    <a href="/burofuchs.apk" className="flex items-center gap-4 px-8 h-16 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-all group cursor-pointer shadow-lg backdrop-blur-md">
                        <div className="bg-emerald-500/20 p-2 rounded-lg text-emerald-400 group-hover:scale-110 transition-transform">
                           <Smartphone size={20} />
                        </div>
                        <div className="text-left">
                           <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('download_apk') || 'Download APK'}</div>
                           <div className="text-sm font-black uppercase tracking-tight text-white">Android</div>
                        </div>
                    </a>
                 </div>
           </motion.div>
        </section>

        {/* TRUST BAR (GÜVENLİK BARİ) */}
        <TrustBar />

        {/* 2. THE ECOSYSTEM VISUAL FEAST (Bento Grid) */}
        <section id="features" className="px-4 md:px-6 relative z-20">
            <div className="max-w-[1400px] mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tighter">{t('ecosystem_title') || "BüroFuchs Ecosystem"}</h2>
                    <p className="text-slate-400 mt-4 text-lg">{t('ecosystem_desc') || "Others offer pieces. We offer the whole picture."}</p>
                </div>

                <motion.div 
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-50px" }}
                    className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(300px,auto)]"
                >
                    
                    {/* A. TAX MODULE (Large - 7 Cols) */}
                    <motion.div variants={itemVariants} className="md:col-span-7 glass-panel p-10 rounded-[3rem] relative overflow-hidden group border-blue-500/30 flex flex-col justify-between shadow-2xl">
                        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-600/20 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2 group-hover:bg-blue-600/30 transition-colors duration-700"></div>
                        <div className="relative z-10">
                             <Badge className="bg-blue-600 text-white border-0 mb-6 font-bold uppercase tracking-widest text-[10px] px-3 py-1 shadow-lg"><PiggyBank size={12} className="mr-2"/> Core Module</Badge>
                             <h3 className="text-4xl md:text-5xl font-black text-white uppercase italic tracking-tight leading-none mb-4">{t('mod_tax_title') || "Max Tax Refund"}</h3>
                             <p className="text-slate-300 font-medium text-lg max-w-md leading-relaxed mb-8">
                                {t('mod_tax_desc') || "Average 1.172€ refund. Our AI engine misses no deductions."}
                             </p>
                        </div>
                        <div className="flex gap-4 mt-8 relative z-10">
                            <div className="bg-[#0B1120]/60 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/10 shadow-lg">
                                <div className="text-2xl font-black text-white">ELSTER</div>
                                <div className="text-[9px] text-slate-500 uppercase font-bold">Official Integration</div>
                            </div>
                            <div className="bg-[#0B1120]/60 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/10 shadow-lg">
                                <div className="text-2xl font-black text-white">OCR</div>
                                <div className="text-[9px] text-slate-500 uppercase font-bold">Smart Scan</div>
                            </div>
                        </div>
                    </motion.div>

                     {/* B. BUREAUCRACY (5 Cols) */}
                     <motion.div variants={itemVariants} className="md:col-span-5 glass-panel p-10 rounded-[3rem] relative overflow-hidden group border-orange-500/30 bg-gradient-to-b from-[#15132b]/50 to-[#0B1120] shadow-2xl">
                        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-orange-600/10 rounded-full blur-[80px] pointer-events-none translate-y-1/2 -translate-x-1/2 group-hover:bg-orange-600/20 transition-colors duration-700"></div>
                        <div className="relative z-10 h-full flex flex-col">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-3 bg-orange-600 rounded-2xl text-white shadow-lg"><Compass size={24}/></div>
                                <span className="text-xs font-black uppercase tracking-widest text-orange-400">Bureaucracy</span>
                            </div>
                            <h3 className="text-3xl font-black text-white uppercase tracking-tight mb-4 leading-none">{t('mod_bureaucracy_title') || "End of Form Chaos"}</h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-8">
                               {t('mod_bureaucracy_desc') || "Anmeldung, Kindergeld, Visa... We simplify the complex German forms."}
                            </p>
                            <div className="grid grid-cols-2 gap-3 mt-auto">
                                {['Anmeldung', 'Visa Assist', 'Kindergeld', 'KFZ Registration'].map((item, i) => (
                                    <div key={i} className="bg-white/5 p-3 rounded-xl border border-white/5 text-[10px] font-bold text-slate-300 uppercase flex items-center justify-center text-center hover:bg-white/10 cursor-default transition-colors">
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* C. INVOICE ENGINE (4 Cols) */}
                    <motion.div variants={itemVariants} className="md:col-span-4 glass-panel p-8 rounded-[3rem] relative overflow-hidden group border-emerald-500/30 shadow-xl">
                         <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                         <div className="relative z-10">
                            <div className="w-12 h-12 bg-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 mb-6 shadow-lg"><FileText size={24}/></div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">{t('mod_invoice_title') || "Invoice Engine"}</h3>
                            <p className="text-slate-400 text-xs font-medium mb-6">{t('mod_invoice_desc') || "Create professional invoices for freelancers."}</p>
                            <div className="w-full h-24 bg-[#0B1120] rounded-xl border border-white/10 p-3 relative opacity-80 shadow-inner">
                                <div className="w-1/3 h-2 bg-emerald-600 rounded-full mb-2"></div>
                                <div className="w-1/2 h-2 bg-slate-700 rounded-full"></div>
                                <div className="absolute right-3 bottom-3 text-emerald-500"><CheckCircle2 size={16}/></div>
                            </div>
                         </div>
                    </motion.div>

                    {/* D. DIGITAL VAULT (4 Cols) */}
                    <motion.div variants={itemVariants} className="md:col-span-4 glass-panel p-8 rounded-[3rem] relative overflow-hidden group border-white/10 shadow-xl">
                         <div className="relative z-10">
                            <div className="w-12 h-12 bg-slate-700/50 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg"><Lock size={24}/></div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">{t('mod_vault_title') || "Digital Vault"}</h3>
                            <p className="text-slate-400 text-xs font-medium mb-6">{t('mod_vault_desc') || "Securely encrypted in Frankfurt (AES-256)."}</p>
                            <div className="flex gap-2">
                                <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[9px] bg-emerald-500/5">GDPR Ready</Badge>
                                <Badge variant="outline" className="border-blue-500/30 text-blue-400 text-[9px] bg-blue-500/5">ISO 27001</Badge>
                            </div>
                         </div>
                    </motion.div>

                    {/* E. MULTI-LANGUAGE (4 Cols) */}
                    <motion.div variants={itemVariants} className="md:col-span-4 glass-panel p-8 rounded-[3rem] relative overflow-hidden group border-purple-500/30 bg-[#111827] shadow-xl">
                         <div className="relative z-10">
                            <div className="w-12 h-12 bg-purple-500/20 rounded-2xl flex items-center justify-center text-purple-400 mb-6 shadow-lg"><Languages size={24}/></div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">{t('mod_lang_title') || "No Language Barrier"}</h3>
                            <p className="text-slate-400 text-xs font-medium mb-6">{t('mod_lang_desc') || "Use the app in your language."}</p>
                            <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl border border-white/5">
                                <span className="text-xl filter grayscale group-hover:grayscale-0 transition-all">🇬🇧</span>
                                <span className="text-xl filter grayscale group-hover:grayscale-0 transition-all">🇹🇷</span>
                                <span className="text-xl filter grayscale group-hover:grayscale-0 transition-all">🇩🇪</span>
                            </div>
                         </div>
                    </motion.div>

                </motion.div>
            </div>
        </section>

        {/* 3. COMPARISON TABLE (Why Us?) */}
        <section className="px-6 relative z-10">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tighter mb-4">{t('why_us_title') || "Why Us?"}</h2>
                    <p className="text-slate-400 font-medium">{t('why_us_desc') || "Competitors do taxes. We simplify your life."}</p>
                </div>

                <div className="glass-panel rounded-[2rem] overflow-hidden border-white/10 shadow-2xl">
                    <div className="grid grid-cols-3 p-6 border-b border-white/10 bg-white/5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <div>Feature</div>
                        <div className="text-center opacity-50">Others</div>
                        <div className="text-center text-blue-400">BüroFuchs</div>
                    </div>
                    {[
                        { name: t('feat_tax_refund') || "Tax Refund", others: true, us: true },
                        { name: t('feat_bureaucracy') || "Bureaucracy", others: false, us: true },
                        { name: t('feat_invoice') || "Invoice Creator", others: false, us: true },
                        { name: t('feat_salary') || "Salary Calculator", others: false, us: true },
                        { name: t('feat_native_lang') || "Native Language", others: false, us: true },
                        { name: t('feat_archive') || "Digital Archive", others: false, us: true },
                    ].map((row, i) => (
                        <div key={i} className="grid grid-cols-3 p-6 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors items-center">
                            <div className="font-bold text-slate-200 text-sm">{row.name}</div>
                            <div className="flex justify-center">
                                {row.others ? <CheckCircle2 size={20} className="text-slate-500"/> : <X size={20} className="text-slate-600 opacity-30"/>}
                            </div>
                            <div className="flex justify-center">
                                <CheckCircle2 size={24} className="text-blue-500 drop-shadow-[0_0_10px_rgba(59,130,246,0.5)]"/>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* 4. PRICING & CTA */}
        <section className="px-6 relative z-10 pb-10">
            <div className="max-w-5xl mx-auto text-center">
                <h2 className="text-3xl md:text-5xl font-black uppercase text-white tracking-tighter mb-6">
                    {t('price_title') || "Fair Price, Max Profit"}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                    {/* STARTER */}
                    <div className="glass-panel p-10 rounded-[3rem] border-white/10 flex flex-col items-center hover:bg-white/5 transition-colors">
                        <h3 className="text-2xl font-black text-white uppercase italic mb-2">{t('plan_starter') || "Starter"}</h3>
                        <div className="text-5xl font-black text-white mb-6">0 €</div>
                        <ul className="space-y-3 mb-8 text-sm text-slate-400 text-left w-full max-w-xs">
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500"/> {t('feat_tax_refund') || "Tax Refund Est."}</li>
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500"/> OCR Scan</li>
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-500"/> Invoice Engine</li>
                        </ul>
                        <Button onClick={() => navigate('/login')} variant="outline" className="w-full h-14 rounded-2xl border-white/10 text-white font-bold uppercase tracking-widest text-xs hover:bg-white/5">{t('btn_try_free') || "Start Now"}</Button>
                    </div>

                    {/* PRO */}
                    <div className="glass-panel p-10 rounded-[3rem] border-blue-500/50 bg-[#111827] flex flex-col items-center relative overflow-hidden shadow-2xl shadow-blue-900/20">
                        <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-black uppercase px-4 py-1 rounded-bl-xl">Recommended</div>
                        <h3 className="text-2xl font-black text-white uppercase italic mb-2">{t('plan_pro') || "Fox AI Pilot"}</h3>
                        <div className="text-5xl font-black text-white mb-2">39,90 €</div>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-6">{t('price_pay_submit') || "Pay only when filing"}</p>
                        <ul className="space-y-3 mb-8 text-sm text-slate-300 text-left w-full max-w-xs">
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-500"/> ELSTER Submission</li>
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-500"/> AI Error Check</li>
                            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-500"/> Digital Mailbox</li>
                        </ul>
                        <Button onClick={() => navigate('/login')} className="w-full h-14 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest text-xs rounded-2xl shadow-lg shadow-blue-900/50">{t('btn_try_free') || "Try Free"}</Button>
                    </div>
                </div>
            </div>
        </section>

      </main>

      <Footer />

      {/* 🔥 TİLKİ GÖRSEL BİLEŞENİ 🔥 */}
      {/* Bu bileşen sayesinde tilki ekranda belirir */}
      <PlayfulFox />

    </div>
  );
};

export default HomePage;